import { Directive, ElementRef, Input, Optional, Renderer2, SimpleChanges } from '@angular/core';

import { isString } from '@skylab-shared/utils';
import { Subject, filter, takeUntil } from 'rxjs';
import { ViewValidationReadonlyItem } from './validate.directive';
import { ValidationSourceDirective } from './validation-source.directive';

type InnerValidationOption = 'disable' | 'disable-control' | 'hide';

@Directive({ selector: '[sklValidateInner]' })
export class ValidateInnerDirective {
    @Input('sklValidateInner') public config!: string | {key: string | null | undefined; option: InnerValidationOption} | undefined;
    @Input('sklValidateInnerReadonly') public readonly = false;

    private validation: ViewValidationReadonlyItem | undefined = { readonly: false };
    private destroy$ = new Subject<void>();

    public constructor(
        private readonly elementRef: ElementRef,
        private readonly renderer: Renderer2,
        @Optional() private readonly source: ValidationSourceDirective
    ) {}

    public ngOnChanges(changes: SimpleChanges): void {
        if (!this.source) return;

        if (changes['readonly']) {
            this.toggleElement();
        } else if (changes['config']) {
            const configChange = changes['config'];
            if (!configChange.firstChange && !isString(configChange.currentValue) && configChange.currentValue?.key !== configChange.previousValue?.key) {
                this.process();
            }
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
    }

    public ngAfterViewInit(): void {
        if (!this.source) return;

        this.process();

        this.source.source.readonlyValidation$
        .pipe(
            takeUntil(this.destroy$),
            filter(elem => elem.key === this.key))
        .subscribe(_ => {
            this.process();
        });
    }

    private process(): void {
        this.validation = this.source.source.readonlyValidations.get(this.key);
        this.toggleElement();
    }

    private get key(): string {
        return isString(this.config) ? this.config : this.config?.key || '';
    }

    private toggleElement(): void {
        if (!this.validation) return;

        const elm = this.elementRef.nativeElement;
        const readonly = this.validation.readonly || this.readonly;

        if (isString(this.config) || this.config?.option === 'hide') {
            readonly
                ? this.renderer.addClass(elm, 'display--none')
                : this.renderer.removeClass(elm, 'display--none');
        } else {
            if (this.config?.option === 'disable') {
                readonly
                    ? this.renderer.addClass(elm, 'text--disabled')
                    : this.renderer.removeClass(elm, 'text--disabled');
            } else if (this.config?.option === 'disable-control') {
                elm.readonly = elm.disabled = readonly;
            }
        }
    }
}
